/**
 * B2B theme behaviour: quantity steppers, AJAX add-to-cart, header cart pill refresh,
 * PDP variant/infusion switching + live price breakdown, reorder-from-order-history,
 * predictive search, and a cart drawer kept in sync via the Section Rendering API.
 * No framework dependency — vanilla JS, progressively enhances plain forms/links
 * so everything still works with JS disabled where a real <form> is present.
 */
(function () {
  "use strict";

  var moneyFormat = window.Shopify && Shopify.money_format ? Shopify.money_format : "€{{amount}}";

  function formatMoney(cents) {
    var value = (cents / 100).toFixed(2).replace(".", ",");
    // simple '{{amount}}' substitution, good enough for EUR-style formats
    return moneyFormat.replace(/\{\{\s*amount\s*\}\}/, value);
  }

  function qs(el, sel) { return el.querySelector(sel); }
  function qsa(el, sel) { return Array.prototype.slice.call(el.querySelectorAll(sel)); }

  /* ---------------- header cart pill ---------------- */
  function refreshCartPill(cart) {
    var pill = document.querySelector("[data-cart-pill]");
    if (!pill || !cart) return;
    var totalEl = qs(pill, "[data-cart-total]");
    var countEl = qs(pill, "[data-cart-count]");
    if (totalEl) totalEl.textContent = formatMoney(cart.total_price);
    // Lines, not units: a 725 g bulk line used to read "726 items" (2026-10-21).
    if (countEl) countEl.textContent = (cart.items || []).length;
  }

  function fetchCart() {
    return fetch("/cart.js", { headers: { Accept: "application/json" } }).then(function (r) { return r.json(); });
  }

  /* ---------------- "already in the cart" badges on the price-list rows ---------------- */
  // Keeps every row badge honest after ANY cart mutation: an add from a row, an add from the
  // PDP, or a quantity change / removal in the drawer. Liquid renders the initial number on
  // page load; from then on this is the only thing that moves it.
  function refreshRowBadges(cart) {
    if (!cart || !cart.items) return;
    // Sum per variant: the same variant can occupy several lines (different line properties),
    // and the buyer cares about the total, not the lines.
    var byVariant = {};
    cart.items.forEach(function (line) {
      byVariant[line.variant_id] = (byVariant[line.variant_id] || 0) + line.quantity;
    });
    qsa(document, "[data-add-btn][data-variant-id]").forEach(function (btn) {
      var badge = qs(btn, "[data-cart-qty]");
      if (!badge) return;
      var n = byVariant[btn.getAttribute("data-variant-id")] || 0;
      badge.textContent = n;
      badge.classList.toggle("is-empty", n === 0);
      // The badge is aria-hidden, so the number has to reach assistive tech through the
      // button's accessible name. The translated template comes from Liquid via
      // [data-incart-tpl] — never build this string in JS, there are five locales.
      var wrap = btn.closest("[data-incart-tpl]");
      var base = btn.getAttribute("title");
      if (wrap && base) {
        var tpl = wrap.getAttribute("data-incart-tpl") || "";
        btn.setAttribute("aria-label", n > 0 ? base + " · " + tpl.replace("__n__", n) : base);
      }
    });
    // The PDP combination table's row buttons use this same [data-add-btn][data-variant-id]
    // contract, so the loop above covers them too.

    // Listing parent rows (products with combinations): the toggle's badge is the product's
    // total across all its combinations, so sum per product instead of per variant.
    var byProduct = {};
    cart.items.forEach(function (line) {
      byProduct[line.product_id] = (byProduct[line.product_id] || 0) + line.quantity;
    });
    qsa(document, "[data-combo-toggle][data-product-id]").forEach(function (btn) {
      var badge = qs(btn, "[data-cart-qty]");
      if (!badge) return;
      var n = byProduct[btn.getAttribute("data-product-id")] || 0;
      badge.textContent = n;
      badge.classList.toggle("is-empty", n === 0);
      var wrap = btn.closest("[data-incart-tpl]");
      var base = btn.getAttribute("title");
      if (wrap && base) {
        var tpl = wrap.getAttribute("data-incart-tpl") || "";
        btn.setAttribute("aria-label", n > 0 ? base + " · " + tpl.replace("__n__", n) : base);
      }
    });
  }

  // Single fan-out for everything derived from the cart, so a new consumer cannot be forgotten
  // at one of the three call sites that mutate it.
  function refreshCartUI(cart) {
    refreshCartPill(cart);
    refreshRowBadges(cart);
  }

  // The row button's content is an <svg> icon: writing its transient state with textContent
  // would delete the icon. Where the button exposes a [data-add-state] span we write there and
  // flag the button .is-busy; the older text-only buttons (PDP) keep the previous behaviour.
  function setAddBtnState(btn, kind) {
    if (!btn) return;
    var slot = qs(btn, "[data-add-state]");
    var label = kind === "adding" ? (btn.dataset.addingLabel || "…")
              : kind === "added" ? (btn.dataset.addedLabel || "✓")
              : "";
    if (slot) {
      slot.textContent = label;
      btn.classList.toggle("is-busy", kind !== "idle");
      return;
    }
    if (kind === "idle") { btn.textContent = btn.dataset.label || btn.textContent; return; }
    btn.dataset.label = btn.dataset.label || btn.textContent;
    btn.textContent = label;
  }

  /* ---------------- generic quantity steppers ---------------- */
  // Any element with [data-qty-stepper] wrapping a <input data-qty-input> plus
  // [data-qty-dec] / [data-qty-inc] buttons. Honours min / max / step from the input.
  function initSteppers(root) {
    qsa(root, "[data-qty-stepper]").forEach(function (stepper) {
      var input = qs(stepper, "[data-qty-input]");
      var dec = qs(stepper, "[data-qty-dec]");
      var inc = qs(stepper, "[data-qty-inc]");
      if (!input) return;
      var step = parseInt(input.step, 10) || 1;
      var min = input.min !== "" ? parseInt(input.min, 10) : step;
      var max = input.max !== "" ? parseInt(input.max, 10) : Infinity;
      // Rule mode — PDP combination table only (see snippets/qty-stepper.liquid). There, 0 is a
      // valid "not ordering this size", and anything above 0 must honour the variant's
      // quantity-rule minimum. Plain clamping with min=0 would let a buyer submit an amount
      // under the minimum, and stepping by `step` would walk through invalid values.
      var ruleMin = input.hasAttribute("data-rule-min") ? (parseInt(input.getAttribute("data-rule-min"), 10) || 0) : null;

      function current() {
        var n = parseInt(input.value, 10);
        if (ruleMin !== null) return isNaN(n) ? 0 : n;
        return n || min; // unchanged for every stepper that is not in rule mode
      }
      function clamp(v) {
        if (ruleMin !== null) {
          if (!(v > 0)) return 0;
          if (v < ruleMin) v = ruleMin;
        }
        v = Math.round(v / step) * step;
        if (v < min) v = min;
        if (v > max) v = max;
        return v;
      }
      function set(v) {
        input.value = clamp(v);
        input.dispatchEvent(new Event("change", { bubbles: true }));
      }
      if (dec) dec.addEventListener("click", function () {
        var cur = current();
        if (ruleMin !== null && cur - step < ruleMin) { set(0); return; } // last "−" goes back to 0
        set(cur - step);
      });
      if (inc) inc.addEventListener("click", function () {
        var cur = current();
        if (ruleMin !== null && cur < ruleMin) { set(ruleMin); return; } // first "+" jumps to the minimum
        set(cur + step);
      });
      input.addEventListener("change", function () { input.value = clamp(current()); });
    });
  }

  /* ---------------- AJAX add to cart (collection rows + PDP) ---------------- */
  function initAddToCart(root) {
    qsa(root, "[data-add-to-cart-form]").forEach(function (form) {
      form.addEventListener("submit", function (e) {
        e.preventDefault();
        var btn = qs(form, "[data-add-btn]");
        // White-label combination row: the logo dialog does the add (see openWlDialog).
        if (form.hasAttribute("data-wl")) {
          var wlQty = qs(form, "[data-qty-input]");
          var wlId = qs(form, 'input[name="id"]');
          openWlDialog({
            variantId: wlId ? wlId.value : "",
            qty: wlQty ? (parseInt(wlQty.value, 10) || 1) : 1,
            productId: form.getAttribute("data-product-id") || "",
            productTitle: form.getAttribute("data-product-title") || "",
            variantTitle: form.getAttribute("data-variant-title") || "",
            btn: btn
          });
          return;
        }
        var formData = new FormData(form);
        if (btn) { btn.disabled = true; setAddBtnState(btn, "adding"); }

        fetch("/cart/add.js", { method: "POST", body: formData, headers: { Accept: "application/json" } })
          .then(function (r) {
            if (!r.ok) return r.json().then(function (err) { throw err; });
            return r.json();
          })
          .then(function () { return fetchCart(); })
          .then(function (cart) {
            refreshCartUI(cart);
            if (btn) { setAddBtnState(btn, "added"); setTimeout(function () { setAddBtnState(btn, "idle"); btn.disabled = false; }, 1200); }
            document.dispatchEvent(new CustomEvent("b2b:cart:added", { detail: { cart: cart } }));
            // Deliberately NOT opening the drawer here. The add is already visible without it:
            // the row shows its in-cart badge, the button flips to "added", and the header pill
            // updates - so throwing a panel over the page just interrupts someone placing a
            // multi-line order. The drawer is one click away on the cart pill, and its toggle
            // calls refreshCartDrawer() first, so it is never stale when opened by hand.
          })
          .catch(function (err) {
            if (btn) { setAddBtnState(btn, "idle"); btn.disabled = false; }
            alert((err && err.description) || "Impossibile aggiungere il prodotto al carrello.");
          });
      });
    });
  }

  /* ---------------- PDP combination table: one cart button per row ---------------- */
  // One row per existing variant (see main-product-b2b.liquid, `use_matrix`). Same behaviour
  // as a price-list row on the collection: the row's stepper holds the amount, its cart button
  // adds just that variant, and the corner badge (refreshRowBadges) shows what is in the cart.
  // Rows are not forms — a <form> cannot sit inside a <tr> — so the button reads its own row's
  // input and posts JSON. The stepper is left where it is after an add, like the listing.
  function initMatrix(root) {
    qsa(root, "[data-matrix-form]").forEach(function (table) {
      qsa(table, "[data-matrix-add][data-variant-id]").forEach(function (btn) {
        btn.addEventListener("click", function () {
          var row = btn.closest("[data-matrix-row]");
          var input = row ? qs(row, "[data-qty-input]") : null;
          var q = input ? (parseInt(input.value, 10) || 0) : 0;
          if (q <= 0 || btn.disabled) return;
          // White-label row: the logo dialog does the add (see openWlDialog).
          if (btn.hasAttribute("data-wl")) {
            openWlDialog({
              variantId: btn.getAttribute("data-variant-id"),
              qty: q,
              productId: btn.getAttribute("data-product-id") || "",
              productTitle: btn.getAttribute("data-product-title") || "",
              variantTitle: btn.getAttribute("data-variant-title") || "",
              btn: btn
            });
            return;
          }
          btn.disabled = true;
          setAddBtnState(btn, "adding");

          fetch("/cart/add.js", {
            method: "POST",
            headers: { "Content-Type": "application/json", Accept: "application/json" },
            body: JSON.stringify({ items: [{ id: parseInt(btn.getAttribute("data-variant-id"), 10), quantity: q }] })
          })
            .then(function (r) {
              if (!r.ok) return r.json().then(function (err) { throw err; });
              return r.json();
            })
            .then(function () { return fetchCart(); })
            .then(function (cart) {
              refreshCartUI(cart);
              setAddBtnState(btn, "added");
              setTimeout(function () { setAddBtnState(btn, "idle"); btn.disabled = false; }, 1200);
              document.dispatchEvent(new CustomEvent("b2b:cart:added", { detail: { cart: cart } }));
            })
            .catch(function (err) {
              setAddBtnState(btn, "idle");
              btn.disabled = false;
              alert((err && err.description) || "Impossibile aggiungere il prodotto al carrello.");
            });
        });
      });
    });
  }

  /* ---------------- listing: open a product's combinations under its row ---------------- */
  // A product with combinations cannot be put in the cart from its own row (which size?), so its
  // row carries a toggle instead (snippets/b2b-product-row.liquid). First click: fetch the rows
  // from sections/b2b-variant-rows.liquid via the Section Rendering API on the product's URL and
  // insert them right under the parent. Later clicks only show/hide them — no refetch, so what
  // the buyer typed in a stepper survives closing and reopening.
  // ⚠ No `Accept: application/json` on this fetch: on a product URL that header makes Shopify
  // answer with the product JSON and ignore `section_id` (same trap as refreshCartDrawer).
  function initComboToggles(root) {
    qsa(root, "[data-combo-toggle]").forEach(function (btn) {
      var row = btn.closest(".b2b-table-row");
      if (!row) return;
      var group = null;
      var loading = false;

      function setOpen(open) {
        btn.setAttribute("aria-expanded", open ? "true" : "false");
        row.classList.toggle("is-open", open);
        if (group) group.hidden = !open;
      }

      btn.addEventListener("click", function () {
        if (loading) return;
        if (group) { setOpen(group.hidden); return; }

        loading = true;
        setAddBtnState(btn, "adding");
        var url = btn.getAttribute("data-url");
        fetch(url + (url.indexOf("?") === -1 ? "?" : "&") + "section_id=b2b-variant-rows")
          .then(function (r) {
            if (!r.ok) throw new Error("HTTP " + r.status);
            return r.text();
          })
          .then(function (html) {
            var doc = new DOMParser().parseFromString(html, "text/html");
            var rows = doc.querySelector("[data-variant-rows]");
            if (!rows) throw new Error("no rows");
            group = document.createElement("div");
            group.className = "b2b-combo-group";
            group.setAttribute("data-combo-group", "");
            group.setAttribute("data-etichetta-scope", ""); // one finish switch per product
            group.innerHTML = rows.innerHTML;
            row.parentNode.insertBefore(group, row.nextSibling);
            initSteppers(group);
            initAddToCart(group);
            initLabelTabs(group);
            loading = false;
            setAddBtnState(btn, "idle");
            setOpen(true);
          })
          .catch(function () {
            loading = false;
            setAddBtnState(btn, "idle");
            // The PDP has the same combinations; better a page change than a dead button.
            window.location.href = url;
          });
      });
    });
  }

  /* ---------------- packed products: finish switch (our brand · unbranded · white label) ---------------- */
  // Rows carry data-etichetta (the model is explained in snippets/label-tabs.liquid). One switch
  // per scope: the PDP table ([data-etichetta-scope] on .b2b-matrix) or one product's
  // combination rows in the listing (the inserted .b2b-combo-group). Starting finish: ?etichetta=
  // in the URL (links from the White Label collection), else the table's data-default-etichetta,
  // else our brand. ⚠ Not `data-label`: setAddBtnState() caches a button's text in that attribute.
  function initLabelTabs(root) {
    qsa(root, "[data-etichetta-tabs]").forEach(function (box) {
      if (box.dataset.b2bWired) return;
      box.dataset.b2bWired = "1";
      var scope = box.closest("[data-etichetta-scope]") || box.parentNode;
      var tabs = qsa(box, "[data-etichetta-tab]");
      if (!tabs.length) return;

      function select(label) {
        var known = tabs.some(function (t) { return t.getAttribute("data-etichetta-tab") === label; });
        if (!known) label = tabs[0].getAttribute("data-etichetta-tab");
        tabs.forEach(function (t) {
          var on = t.getAttribute("data-etichetta-tab") === label;
          t.setAttribute("aria-pressed", on ? "true" : "false");
          t.classList.toggle("is-active", on);
        });
        qsa(scope, "[data-etichetta]").forEach(function (row) {
          row.hidden = row.getAttribute("data-etichetta") !== label;
        });
        qsa(box, "[data-etichetta-note]").forEach(function (n) {
          n.hidden = n.getAttribute("data-etichetta-note") !== label;
        });
      }

      tabs.forEach(function (t) {
        t.addEventListener("click", function () { select(t.getAttribute("data-etichetta-tab")); });
      });
      var fromUrl = null;
      try { fromUrl = new URLSearchParams(window.location.search).get("etichetta"); } catch (e) { /* old browser */ }
      var holder = box.closest("[data-default-etichetta]");
      select(fromUrl || (holder && holder.getAttribute("data-default-etichetta")) || "marchio");
    });
  }

  /* ---------------- white label: optional logo + graphic-design service ---------------- */
  // A white-label combination ([data-wl] on its row form or table button) is never added straight
  // to the cart: the button opens the dialog (snippets/wl-dialog.liquid), where the buyer MAY attach
  // a logo and print notes — both optional (owner's rule). Confirming posts the variant as
  // multipart FormData — Shopify accepts it on /cart/add.js: an attached file goes to its CDN and
  // the property becomes the URL — with a hidden `_wl` marker.
  // The graphic-design service is ONE line PER PRODUCT bought in white label (owner's rule:
  // Amnesia in three sizes = one service, Amnesia + Nepal = two), tied to its product by a hidden
  // `_wl_product` property. syncWlService() keeps that true after every cart change: a product
  // without its service gets one, a service whose product left the cart goes, duplicates go, the
  // quantity stays 1. A cart line counts as white label by its `_wl` marker OR by its SKU ending in
  // "-wl" (every white-label variant's SKU is the brand SKU + "-wl"), so lines that reach the cart
  // another way (reorder, a cart from before this change) are covered too.
  // ℹ Enforced by this script, not by Shopify: it holds for everything done through the theme,
  // which is the only way buyers reach the cart.
  var wl = null; // { dialog, form, file, note, error, summary, confirm, serviceNew, serviceIncluded, ctx }

  function wlConfig(name) { return wl && wl.dialog ? (wl.dialog.getAttribute("data-" + name) || "") : ""; }
  function wlServiceVariant() { return parseInt(wlConfig("service-variant"), 10) || 0; }

  function isWlLine(line, service) {
    if (!line || line.variant_id === service) return false;
    if (line.properties && line.properties._wl) return true;
    return /-wl$/i.test(line.sku || "");
  }

  function hasWlService(cart, productId, service) {
    return !!(cart && cart.items || []).filter(function (l) {
      return l.variant_id === service && l.properties && String(l.properties._wl_product) === String(productId);
    }).length;
  }

  function wlError(msg) {
    if (!wl) return;
    wl.error.textContent = msg || "";
    wl.error.hidden = !msg;
  }

  // Which service sentence the dialog shows: "added once for this product" or "already in your cart".
  function wlShowServiceState(included) {
    if (wl.serviceNew) wl.serviceNew.hidden = !!included;
    if (wl.serviceIncluded) wl.serviceIncluded.hidden = !included;
  }

  function openWlDialog(ctx) {
    if (!wl || !wl.dialog || typeof wl.dialog.showModal !== "function") {
      alert("Impossibile aprire la personalizzazione: aggiorna la pagina e riprova.");
      return;
    }
    if (wl.busy) return; // an add is still running in the open dialog
    wl.ctx = ctx;
    wl.form.reset();
    wlError("");
    wl.confirm.disabled = false;
    wl.summary.textContent = ctx.qty + " × " + ctx.productTitle + (ctx.variantTitle ? " — " + ctx.variantTitle : "");
    wlShowServiceState(false);
    wl.dialog.showModal();
    var service = wlServiceVariant();
    if (service && ctx.productId) {
      fetchCart()
        .then(function (cart) {
          if (wl.ctx === ctx) wlShowServiceState(hasWlService(cart, ctx.productId, service));
        })
        .catch(function () { /* keep the generic sentence */ });
    }
  }

  function initWlDialog() {
    var dialog = document.getElementById("WlDialog");
    if (!dialog) return;
    wl = {
      dialog: dialog,
      form: qs(dialog, "[data-wl-form]"),
      file: qs(dialog, "[data-wl-file]"),
      note: qs(dialog, "[data-wl-note]"),
      error: qs(dialog, "[data-wl-error]"),
      summary: qs(dialog, "[data-wl-summary]"),
      confirm: qs(dialog, "[data-wl-confirm]"),
      serviceNew: qs(dialog, "[data-wl-service-new]"),
      serviceIncluded: qs(dialog, "[data-wl-service-included]"),
      cancel: qs(dialog, "[data-wl-cancel]"),
      busy: false, // true while an add is in flight: the dialog cannot be closed or reused
      ctx: null
    };
    // Closing mid-add would not stop the request, and its answer would then land on whatever the
    // dialog shows next, so Cancel and Esc wait until the add has finished.
    wl.cancel.addEventListener("click", function () { if (!wl.busy) dialog.close(); });
    dialog.addEventListener("cancel", function (e) { if (wl.busy) e.preventDefault(); });
    function setBusy(on) {
      wl.busy = on;
      wl.confirm.disabled = on;
      wl.cancel.disabled = on;
    }

    wl.form.addEventListener("submit", function (e) {
      e.preventDefault();
      var ctx = wl.ctx;
      if (!ctx) return;
      var file = wl.file.files && wl.file.files[0]; // optional
      var maxBytes = parseInt(wlConfig("max-bytes"), 10) || 10485760;
      if (file && file.size > maxBytes) { wlError(wlConfig("msg-too-big")); return; }
      wlError("");
      setBusy(true);
      if (ctx.btn) { ctx.btn.disabled = true; setAddBtnState(ctx.btn, "adding"); }

      var fd = new FormData();
      fd.append("id", ctx.variantId);
      fd.append("quantity", ctx.qty);
      if (file) fd.append("properties[" + wlConfig("prop-logo") + "]", file, file.name);
      var noteText = (wl.note.value || "").trim();
      if (noteText) fd.append("properties[" + wlConfig("prop-note") + "]", noteText);
      fd.append("properties[_wl]", "1");

      fetch("/cart/add.js", { method: "POST", body: fd, headers: { Accept: "application/json" } })
        .then(function (r) {
          if (!r.ok) return r.json().then(function (err) { throw err; });
          return r.json();
        })
        .then(function () { return fetchCart(); })
        .then(syncWlService) // adds this product's service line if it is not in the cart yet
        .then(function (cart) {
          setBusy(false);
          refreshCartUI(cart);
          // The product is in the cart either way; a missing service is a store set-up problem
          // (the service product not published in this buyer's catalog), not the buyer's — say it
          // where the owner will look, without blocking the order.
          var service = wlServiceVariant();
          if (ctx.productId && (!service || !hasWlService(cart, ctx.productId, service))) {
            console.warn("[b2b] white-label graphic service NOT in the cart for product " + ctx.productId +
                         (service ? " (service variant " + service + " refused — is it in this buyer's catalog?)" : " (service product unavailable)"));
          }
          if (wl.ctx === ctx) wl.dialog.close();
          if (ctx.btn) {
            setAddBtnState(ctx.btn, "added");
            setTimeout(function () { setAddBtnState(ctx.btn, "idle"); ctx.btn.disabled = false; }, 1200);
          }
          document.dispatchEvent(new CustomEvent("b2b:cart:added", { detail: { cart: cart } }));
        })
        .catch(function (err) {
          setBusy(false);
          if (ctx.btn) { setAddBtnState(ctx.btn, "idle"); ctx.btn.disabled = false; }
          if (wl.ctx === ctx) wlError((err && (err.description || err.message)) || "Impossibile aggiungere il prodotto al carrello.");
        });
    });
  }

  // Resolves with the cart as it is AFTER the repair; `__wlChanged` tells the caller whether
  // anything had to be fixed (the cart page then reloads, the drawer re-renders anyway).
  function syncWlService(cart) {
    var service = wlServiceVariant();
    if (!service || !cart || !cart.items) return Promise.resolve(cart);
    var products = {}, services = {}, updates = {}, adds = [], changed = false;
    cart.items.forEach(function (line) {
      if (line.variant_id === service) {
        var pid = line.properties && line.properties._wl_product;
        if (!pid || services[pid]) { updates[line.key] = 0; changed = true; return; } // untied or a second one
        services[pid] = line;
      } else if (isWlLine(line, service)) {
        var id = String(line.product_id);
        if (!products[id]) products[id] = line.product_title;
      }
    });
    Object.keys(services).forEach(function (pid) {
      var s = services[pid];
      if (!products[pid]) { updates[s.key] = 0; changed = true; }
      else if (s.quantity !== 1) { updates[s.key] = 1; changed = true; }
    });
    Object.keys(products).forEach(function (pid) {
      if (services[pid]) return;
      var props = { _wl_product: pid };
      props[wlConfig("prop-for")] = products[pid];
      adds.push({ id: service, quantity: 1, properties: props });
      changed = true;
    });
    if (!changed) return Promise.resolve(cart);

    // "Changed" is judged on the cart Shopify returns, not on what was attempted: if the service
    // cannot be added (product missing from a buyer's catalog), nothing moves, and the cart page
    // must not reload forever trying.
    function signature(c) { return (c.items || []).map(function (l) { return l.key + ":" + l.quantity; }).join("|"); }
    var before = signature(cart);
    var json = { "Content-Type": "application/json", Accept: "application/json" };
    var chain = Promise.resolve();
    if (Object.keys(updates).length) {
      chain = chain.then(function () {
        return fetch("/cart/update.js", { method: "POST", headers: json, body: JSON.stringify({ updates: updates }) });
      });
    }
    if (adds.length) {
      chain = chain.then(function () {
        return fetch("/cart/add.js", { method: "POST", headers: json, body: JSON.stringify({ items: adds }) });
      });
    }
    return chain
      .then(function () { return fetchCart(); })
      .then(function (fresh) { fresh.__wlChanged = signature(fresh) !== before; return fresh; });
  }

  /* ---------------- reorder from account order history ---------------- */
  function initReorder(root) {
    qsa(root, "[data-reorder]").forEach(function (btn) {
      btn.addEventListener("click", function () {
        var items;
        try { items = JSON.parse(btn.getAttribute("data-reorder")); } catch (e) { return; }
        if (!items || !items.length) return;
        btn.disabled = true;
        var label = btn.textContent;
        btn.textContent = "…";
        fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ items: items })
        })
          .then(function (r) { return r.json(); })
          .then(function () { return fetchCart(); })
          // A reordered white-label line comes back without `_wl` (only id + quantity), but its
          // "-wl" SKU still marks it: give its product the graphic service right away, so the
          // header total is right before the buyer even opens the cart.
          .then(syncWlService)
          .then(function (cart) {
            refreshCartUI(cart);
            btn.textContent = "✓ Aggiunto";
            setTimeout(function () { btn.textContent = label; btn.disabled = false; }, 1500);
            // Same reasoning as the add path above. ⚠️ Note reorder is the weaker case: it adds
            // several lines at once from the account page, where there is no per-row badge - the
            // only feedback is this button and the header pill. If reorder should still pop the
            // drawer, this is the one line to put back.
          })
          .catch(function () {
            btn.textContent = label; btn.disabled = false;
            alert("Impossibile riordinare automaticamente. Alcuni articoli potrebbero non essere più disponibili.");
          });
      });
    });
  }

  /* ---------------- PDP: media gallery (scroll-snap track + thumbs + zoom) ---------------- */
  // The main viewer is a native CSS scroll-snap track (one slide per product media item),
  // so touch swipe on mobile needs no JS at all. This just keeps the active thumbnail in
  // sync with whichever slide is scrolled into view, wires the prev/next arrows and
  // thumbnail clicks to scroll the track, and drives a simple image zoom lightbox.
  function initGallery(root) {
    var gallery = qs(root, "[data-pdp-gallery]");
    if (!gallery) return;
    var track = qs(gallery, "[data-pdp-track]");
    var slides = qsa(gallery, "[data-pdp-slide]");
    var thumbs = qsa(gallery, "[data-pdp-thumb]");
    var prevBtn = qs(gallery, "[data-pdp-prev]");
    var nextBtn = qs(gallery, "[data-pdp-next]");
    if (!track || !slides.length) return;

    function setActiveThumb(index) {
      thumbs.forEach(function (t, i) {
        t.classList.toggle("is-active", i === index);
        t.setAttribute("aria-selected", i === index ? "true" : "false");
      });
    }

    function goTo(index) {
      index = Math.max(0, Math.min(index, slides.length - 1));
      slides[index].scrollIntoView({ behavior: "smooth", inline: "start", block: "nearest" });
      setActiveThumb(index);
    }

    function currentIndex() {
      return Math.round(track.scrollLeft / (track.clientWidth || 1));
    }

    thumbs.forEach(function (thumb, i) {
      thumb.addEventListener("click", function () { goTo(i); });
    });
    if (prevBtn) prevBtn.addEventListener("click", function () { goTo(currentIndex() - 1); });
    if (nextBtn) nextBtn.addEventListener("click", function () { goTo(currentIndex() + 1); });

    // swiping the track directly (touch, trackpad, or scrollbar) should also
    // update the active thumbnail — debounced since "scroll" fires continuously
    var scrollTimer = null;
    track.addEventListener("scroll", function () {
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(function () { setActiveThumb(currentIndex()); }, 80);
    });

    /* click-to-zoom lightbox — images only; video/model slides play/orbit inline */
    var modal = qs(document, "[data-pdp-zoom-modal]");
    var modalImg = modal && qs(modal, "[data-pdp-zoom-image]");
    function closeZoom() {
      modal.hidden = true;
      document.documentElement.style.overflow = "";
    }
    if (modal && modalImg) {
      qsa(gallery, "[data-pdp-zoom-trigger]").forEach(function (trigger) {
        trigger.addEventListener("click", function () {
          modalImg.src = trigger.getAttribute("data-full-src") || "";
          modalImg.alt = trigger.getAttribute("data-alt") || "";
          modal.hidden = false;
          document.documentElement.style.overflow = "hidden";
        });
      });
      qsa(modal, "[data-pdp-zoom-close]").forEach(function (el) {
        el.addEventListener("click", closeZoom);
      });
      document.addEventListener("keydown", function (e) {
        if (e.key === "Escape" && !modal.hidden) closeZoom();
      });
    }
  }

  /* ---------------- PDP: variant (infusion) + tier + qty => live totals ---------------- */
  function initProductForm(root) {
    var form = qs(root, "[data-product-form]");
    if (!form) return;
    var variantsJson = form.getAttribute("data-variants");
    var variants;
    try { variants = JSON.parse(variantsJson); } catch (e) { variants = []; }

    var qtyInput = qs(form, "[data-qty-input]");
    var variantInput = qs(form, "[data-variant-id-input]");
    var priceNow = qs(root, "[data-price-now]");
    var addBtn = qs(form, "[data-add-btn]");

    // The quantity rule and the tier prices come from data- attributes, NOT from data-variants:
    // `variant | json` does not serialize `quantity_price_breaks` or `quantity_rule`, so reading
    // them off the variant payload silently yielded undefined.
    var entryMin = parseInt(form.getAttribute("data-entry-min"), 10) || 1;
    var entryIncrement = parseInt(form.getAttribute("data-entry-increment"), 10) || 1;
    var entryMax = parseInt(form.getAttribute("data-entry-max"), 10) || 0;
    var basePriceAttr = digits(form.getAttribute("data-base-price"));

    // Money filters can emit leading zeros ("075" for €0,75) and non-breaking spaces, so read
    // every price attribute through this rather than parseInt/Number on the raw string. An
    // earlier version put these in a JSON attribute, where "075" is invalid JSON: JSON.parse
    // threw, the catch produced an empty list, and the page silently charged the base price at
    // every quantity. Digits-only parsing has no such cliff.
    function digits(x) { return parseInt(String(x == null ? "" : x).replace(/[^0-9]/g, ""), 10) || 0; }

    // The tier table IS the buttons. Reading it from the DOM means the arithmetic can never
    // disagree with the per-unit price the buyer is looking at, and there is nothing to parse.
    // Sorted ascending so the last match wins regardless of markup order.
    function tiers() {
      return qsa(root, "[data-tier-option]")
        .map(function (t) {
          return { min: digits(t.getAttribute("data-tier-min")), price: digits(t.getAttribute("data-tier-price")) };
        })
        .filter(function (t) { return t.min > 0 && t.price > 0; })
        .sort(function (a, b) { return a.min - b.min; });
    }
    // Unit price actually payable at this quantity: the highest tier whose minimum is met.
    function unitFor(v, qty) {
      var unit = v && v.price != null ? v.price : basePriceAttr;
      tiers().forEach(function (t) { if (qty >= t.min) unit = t.price; });
      return unit;
    }

    function currentVariant() {
      var id = parseInt(variantInput.value, 10);
      for (var i = 0; i < variants.length; i++) if (variants[i].id === id) return variants[i];
      return variants[0];
    }

    function recalc() {
      var qty = parseInt(qtyInput.value, 10) || 1;
      var v = currentVariant();
      if (!v) return;

      // The three totals boxes were removed from the PDP, so nothing derived from them is
      // computed any more -- no dead arithmetic left behind. The per-unit price is still
      // driven by the quantity, since that is what the tier tiles change.
      var unit = unitFor(v, qty);
      if (priceNow) priceNow.textContent = formatMoney(unit) + (form.getAttribute("data-unit-suffix") || "");

      if (addBtn) addBtn.disabled = !v.available;

      // Keep the stepper's bounds on the quantity rule. Liquid already set them when rendering,
      // and `quantity_rule` is absent from data-variants, so these come from the same
      // data-entry-* attributes rather than from the variant payload.
      qtyInput.min = entryMin;
      qtyInput.step = entryIncrement;
      if (entryMax) qtyInput.max = entryMax;

      // highlight the matching volume-pricing tier
      qsa(root, "[data-tier-option]").forEach(function (t) {
        var min = parseInt(t.getAttribute("data-tier-min"), 10);
        t.classList.toggle("is-active", min === closestBreak(v, qty));
      });
    }

    // Which tier tile matches this quantity. The entry tier is its own tile carrying
    // data-tier-min = the quantity rule's minimum, so when no higher tier is met the answer is
    // that minimum. Returning 0 (as this used to) matched no tile at all, which is why the
    // first tier never appeared selected.
    function closestBreak(v, qty) {
      var m = entryMin;
      tiers().forEach(function (t) { if (qty >= t.min) m = t.min; });
      return m;
    }

    qsa(root, "[data-tier-option]").forEach(function (t) {
      t.addEventListener("click", function () {
        qtyInput.value = t.getAttribute("data-tier-min");
        recalc();
      });
    });

    qsa(root, "[data-infusion-option]").forEach(function (opt) {
      opt.addEventListener("click", function () {
        qsa(root, "[data-infusion-option]").forEach(function (o) { o.classList.remove("is-active"); });
        opt.classList.add("is-active");
        variantInput.value = opt.getAttribute("data-variant-id");
        // The add button's in-cart badge is per variant: point it at the new one and recount.
        if (addBtn) {
          addBtn.setAttribute("data-variant-id", variantInput.value);
          fetchCart().then(refreshRowBadges).catch(function () {});
        }
        recalc();
      });
    });

    // Fail loudly, not silently. Every previous incarnation of this bug looked identical from
    // the outside: the tier data went missing and the page quietly charged the base price at
    // every quantity, which reads as "working". If there are tier buttons but none of them
    // yielded usable numbers, the attributes are broken -- say so.
    if (qsa(root, "[data-tier-option]").length && !tiers().length) {
      console.warn("[b2b] tier buttons present but no usable data-tier-min/data-tier-price; " +
                   "volume pricing will fall back to the base price at every quantity");
    }

    qtyInput.addEventListener("change", recalc);
    recalc();
  }

  /* ---------------- shared: change/remove a cart line via /cart/change.js ---------------- */
  // Used by both the full cart page and the cart drawer — whichever DOM the changed line
  // lives in gets its own total patched in place; the header pill and the drawer (if it
  // exists elsewhere on the page) are always kept in sync too.
  function changeCartLine(key, quantity, changedLine) {
    return fetch("/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ id: key, quantity: quantity })
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        // A refused change (e.g. under a quantity-rule minimum) answers with an error, not a cart:
        // let the caller's catch reload the page, which shows what the cart really holds.
        if (!cart || !cart.items) throw cart;
        return syncWlService(cart); // removing a product's last white-label line takes its service with it
      })
      .then(function (cart) {
        if (cart.__wlChanged && document.body.classList.contains("template-cart")) {
          window.location.reload();
          return cart;
        }
        refreshCartUI(cart);

        qsa(document, "[data-cart-subtotal], [data-cart-total-summary]").forEach(function (el) {
          el.textContent = formatMoney(cart.total_price);
        });

        if (changedLine) {
          var match = null;
          for (var i = 0; i < cart.items.length; i++) {
            if (cart.items[i].key === key) { match = cart.items[i]; break; }
          }
          if (match) {
            var lineTotalEl = qs(changedLine, "[data-line-total]");
            if (lineTotalEl) lineTotalEl.textContent = formatMoney(match.final_line_price);
            // Per-gram price follows the volume tier the new quantity falls in.
            var lineUnitEl = qs(changedLine, "[data-line-unit]");
            if (lineUnitEl) lineUnitEl.textContent = formatMoney(match.final_price) + (lineUnitEl.getAttribute("data-unit-suffix") || "");
          } else {
            changedLine.remove();
          }
        }

        refreshCartDrawer();

        // Only the standalone cart page needs a full reload to swap in its own
        // empty-state markup; the drawer just re-renders itself via refreshCartDrawer().
        if (cart.item_count === 0 && document.body.classList.contains("template-cart")) {
          window.location.reload();
        }
        return cart;
      });
  }

  // Wires quantity-stepper + remove-button listeners for every [data-cart-line] found
  // inside `container`. Callable repeatedly — used at page load and again after the
  // drawer's content is swapped in with fresh markup that needs its own listeners.
  function wireCartLines(container) {
    qsa(container, "[data-cart-line]").forEach(function (line) {
      var key = line.getAttribute("data-line-key");
      var qtyInput = qs(line, "[data-qty-input]");
      var removeBtn = qs(line, "[data-cart-remove]");

      if (qtyInput && !qtyInput.dataset.b2bWired) {
        qtyInput.dataset.b2bWired = "1";
        qtyInput.addEventListener("change", function () {
          var qty = parseInt(qtyInput.value, 10) || 0;
          changeCartLine(key, qty, line).catch(function () { window.location.reload(); });
        });
      }
      if (removeBtn && !removeBtn.dataset.b2bWired) {
        removeBtn.dataset.b2bWired = "1";
        removeBtn.addEventListener("click", function () {
          changeCartLine(key, 0, line).catch(function () { window.location.reload(); });
        });
      }
    });
  }

  function initCartPage(root) {
    wireCartLines(root);
    // On /cart, repair the white-label service lines once on load (e.g. after the plain-form
    // "Update cart" submit) and reload only if Shopify's cart actually changed.
    if (document.body.classList.contains("template-cart")) {
      fetchCart()
        .then(syncWlService)
        .then(function (cart) { if (cart && cart.__wlChanged) window.location.reload(); })
        .catch(function () { /* page already shows the server's cart */ });
    }
  }

  /* ---------------- cart drawer ---------------- */
  function refreshCartDrawer() {
    var drawer = document.getElementById("CartDrawer");
    var content = drawer && qs(drawer, "[data-cart-drawer-content]");
    if (!content) return Promise.resolve();

    var url = window.location.pathname + window.location.search;
    var sep = url.indexOf("?") === -1 ? "?" : "&";

    // NO Accept: application/json here, on purpose. The Section Rendering API answers on the
    // page's own URL, and on a product or collection URL that header makes Shopify serve the
    // PRODUCT/COLLECTION resource instead - `?sections=` is ignored and the reply comes back as
    // { "product": {...} } / { "collection": {...} }. `data["cart-drawer-b2b"]` is then
    // undefined, the swap below is skipped, and the drawer opens still showing whatever was
    // rendered at page load - i.e. "Your cart is empty" right after an add. Fetch it plain;
    // the endpoint returns JSON regardless. (/cart.js, /cart/add.js, /cart/change.js and
    // /search/suggest.json are real JSON endpoints and DO keep the header.)
    return fetch(url + sep + "sections=cart-drawer-b2b")
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var html = data && data["cart-drawer-b2b"];
        if (!html) return;
        var doc = new DOMParser().parseFromString(html, "text/html");
        var fresh = doc.querySelector("[data-cart-drawer-content]");
        if (fresh) {
          content.innerHTML = fresh.innerHTML;
          initSteppers(content);
          wireCartLines(content);
        }
      });
  }

  function initCartDrawer() {
    var drawer = document.getElementById("CartDrawer");
    if (!drawer) return;

    function open() {
      drawer.setAttribute("aria-hidden", "false");
      document.documentElement.style.overflow = "hidden";
    }
    function close() {
      drawer.setAttribute("aria-hidden", "true");
      document.documentElement.style.overflow = "";
    }

    qsa(document, "[data-cart-drawer-toggle]").forEach(function (toggle) {
      toggle.addEventListener("click", function (e) {
        e.preventDefault();
        // Repair the white-label service lines first (cheap no-op when there is nothing to fix), so
        // the drawer never shows a service without its product or the other way round.
        fetchCart()
          .then(syncWlService)
          .then(function (cart) { if (cart && cart.__wlChanged) refreshCartUI(cart); })
          .catch(function () { /* the drawer still refreshes below */ })
          .then(refreshCartDrawer);
        open();
      });
    });
    qsa(drawer, "[data-cart-drawer-close]").forEach(function (el) {
      el.addEventListener("click", function (e) {
        e.preventDefault();
        close();
      });
    });
    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape" && drawer.getAttribute("aria-hidden") === "false") close();
    });

    window.__b2bCartDrawerOpen = open;
    wireCartLines(drawer);
  }

  /* ---------------- mobile menu drawer ---------------- */
  // Same open/close shape as the cart drawer above. The panel is in the DOM at every screen
  // size (CSS hides it above the breakpoint), so there is nothing to wire conditionally.
  function initMobileMenu() {
    var menu = document.getElementById("MobileMenu");
    if (!menu) return;
    var toggles = qsa(document, "[data-mobile-menu-toggle]");

    function setOpen(isOpen) {
      menu.setAttribute("aria-hidden", isOpen ? "false" : "true");
      document.documentElement.style.overflow = isOpen ? "hidden" : "";
      toggles.forEach(function (t) { t.setAttribute("aria-expanded", isOpen ? "true" : "false"); });
    }

    toggles.forEach(function (toggle) {
      toggle.addEventListener("click", function (e) { e.preventDefault(); setOpen(true); });
    });
    qsa(menu, "[data-mobile-menu-close]").forEach(function (el) {
      el.addEventListener("click", function (e) { e.preventDefault(); setOpen(false); });
    });
    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape" && menu.getAttribute("aria-hidden") === "false") setOpen(false);
    });
  }

  // ⚠️ Intentionally NOT called any more (2026-09-08): auto-opening on every add was removed.
  // Kept because it is the whole implementation of "refresh, then open", which is what any future
  // caller would need; `window.__b2bCartDrawerOpen` exists for the same reason.
  function openCartDrawerIfEnabled() {
    if (!document.querySelector("[data-cart-drawer-toggle]")) return;
    refreshCartDrawer().then(function () {
      if (window.__b2bCartDrawerOpen) window.__b2bCartDrawerOpen();
    });
  }

  /* ---------------- predictive search ---------------- */
  // NOTE: this deliberately calls the raw `/search/suggest.json` resources API and builds
  // the dropdown markup here in JS, instead of fetching the section-rendered HTML via
  // `?section_id=predictive-search-b2b` (the Section Rendering API). That HTML-rendering
  // path was found to reliably return zero results for every query on this store even
  // when the JSON resources API and the full /search page both find the same product —
  // a platform-side inconsistency, not a bug in this theme's Liquid/JS. Calling the JSON
  // endpoint directly sidesteps it. Trade-off: the JSON resources API only exposes core
  // product fields (title, price, image, url, availability) — no metafields — so the
  // dropdown can't show the segment/unit-label detail the old section markup had.
  function initPredictiveSearch(root) {
    var form = qs(root, "[data-predictive-search-form]");
    if (!form) return;
    var input = qs(form, "[data-predictive-search-input]");
    var results = qs(form.parentNode, "[data-predictive-search-results]");
    var baseUrl = form.getAttribute("data-predictive-search-url");
    if (!input || !results || !baseUrl) return;

    var noResultsLabel = form.getAttribute("data-predictive-search-no-results") || "No results found.";
    var viewAllTemplate = form.getAttribute("data-predictive-search-view-all") || "";
    var searchUrl = form.getAttribute("data-predictive-search-search-url") || "/search";
    // Guest gate, dropdown edition: Liquid sets this attribute only for a signed-in customer.
    // Without it the dropdown showed every guest the price (found 2026-09-28).
    var showPrice = form.hasAttribute("data-predictive-show-price");
    var jsonUrl = baseUrl.replace(/\/$/, "") + ".json";

    var debounceTimer = null;
    var currentController = null;

    function close() {
      results.hidden = true;
      results.innerHTML = "";
      input.setAttribute("aria-expanded", "false");
    }

    function escapeHtml(value) {
      return String(value == null ? "" : value).replace(/[&<>"']/g, function (c) {
        return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
      });
    }

    function productThumb(product) {
      if (!product.image) return '<span class="b2b-predictive-thumb" aria-hidden="true"></span>';
      var sep = product.image.indexOf("?") === -1 ? "?" : "&";
      var src = product.image + sep + "width=96";
      return '<img src="' + escapeHtml(src) + '" alt="' + escapeHtml(product.title) + '" loading="lazy" class="b2b-predictive-thumb">';
    }

    function render(term, products) {
      if (!products.length) {
        results.innerHTML = '<p class="b2b-predictive-empty">' + escapeHtml(noResultsLabel) + "</p>";
        return;
      }

      var html = '<ul class="b2b-predictive-list" role="listbox">';
      products.forEach(function (product) {
        var price = showPrice ? formatMoney(Math.round(parseFloat(product.price_min || product.price || 0) * 100)) : "";
        html +=
          '<li role="option"><a href="' + escapeHtml(product.url) + '" class="b2b-predictive-item">' +
          productThumb(product) +
          '<span class="b2b-predictive-meta"><span class="name">' + escapeHtml(product.title) + "</span></span>" +
          (showPrice ? '<span class="price">' + price + "</span>" : "") +
          "</a></li>";
      });
      html += "</ul>";

      if (viewAllTemplate) {
        var label = viewAllTemplate.replace("__TERM__", escapeHtml(term));
        html +=
          '<a href="' + searchUrl + "?q=" + encodeURIComponent(term) + '&type=product" class="b2b-predictive-viewall">' +
          label +
          "</a>";
      }

      results.innerHTML = html;
    }

    function search(term) {
      if (currentController) currentController.abort();
      currentController = window.AbortController ? new AbortController() : null;

      var url =
        jsonUrl +
        "?q=" + encodeURIComponent(term) +
        "&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=last";

      fetch(url, { signal: currentController && currentController.signal, headers: { Accept: "application/json" } })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          var products = (data && data.resources && data.resources.results && data.resources.results.products) || [];
          render(term, products);
          results.hidden = false;
          input.setAttribute("aria-expanded", "true");
        })
        .catch(function () { /* aborted or offline — leave previous results as-is */ });
    }

    input.addEventListener("input", function () {
      var term = input.value.trim();
      clearTimeout(debounceTimer);
      if (term.length < 2) { close(); return; }
      debounceTimer = setTimeout(function () { search(term); }, 250);
    });

    input.addEventListener("focus", function () {
      if (input.value.trim().length >= 2 && results.innerHTML.trim()) results.hidden = false;
    });

    document.addEventListener("click", function (e) {
      if (!form.parentNode.contains(e.target)) close();
    });
    input.addEventListener("keydown", function (e) {
      if (e.key === "Escape") close();
    });
  }

  document.addEventListener("DOMContentLoaded", function () {
    initSteppers(document);
    initAddToCart(document);
    initReorder(document);
    initGallery(document);
    initProductForm(document);
    initMatrix(document);
    initComboToggles(document);
    initLabelTabs(document);
    initWlDialog(); // before initCartPage: syncWlService reads the service variant from the dialog
    initCartPage(document);
    initCartDrawer();
    initMobileMenu();
    initPredictiveSearch(document);
  });
})();
